import { useState } from "react";
import { api } from "@/lib/api";
import { UploadSimple, CloudArrowDown, CheckCircle, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Import() {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [syncResult, setSyncResult] = useState(null);

  const upload = async () => {
    if (!file) return toast.error("Selecione um arquivo CSV");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/contracts/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success(`${data.imported} contratos importados`);
    } catch (e) {
      toast.error("Erro na importação");
    } finally {
      setBusy(false);
    }
  };

  const syncTCE = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/tce/sync");
      setSyncResult(data);
      if (data.ok) toast.success(`Sincronização: ${data.count} registros`);
      else toast.message("API TCE-CE indisponível", { description: data.message });
    } catch {
      toast.error("Erro na sincronização");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-8 max-w-5xl">
      <div>
        <div className="uppercase-label">Carga de dados</div>
        <h1 className="font-heading text-4xl tracking-tight mt-2">Importar Contratos</h1>
        <p className="text-sm text-[#94A3B8] mt-2">
          Importe contratos via planilha CSV ou sincronize diretamente com a API pública do TCE-CE.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* CSV Upload */}
        <div className="tce-card p-7">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-md bg-[#1E293B] flex items-center justify-center text-[#F59E0B]">
              <UploadSimple size={20} weight="duotone" />
            </div>
            <div>
              <div className="uppercase-label">Planilha</div>
              <div className="font-heading text-xl mt-1">Upload de CSV</div>
            </div>
          </div>

          <label className="block border-2 border-dashed border-[#334155] hover:border-[#F59E0B]
                            rounded-md p-8 text-center cursor-pointer transition-colors"
                 data-testid="csv-upload-area">
            <input type="file" accept=".csv" className="hidden"
                   data-testid="csv-input"
                   onChange={(e) => setFile(e.target.files?.[0] || null)} />
            <UploadSimple size={36} weight="duotone" className="text-[#F59E0B] mx-auto mb-3" />
            <div className="text-sm text-[#E9EEF5]">
              {file ? file.name : "Clique para selecionar um arquivo CSV"}
            </div>
            <div className="text-xs text-[#94A3B8] mt-2">
              Aceita UTF-8/Latin-1, delimitador <code className="text-[#F59E0B]">;</code> ou <code className="text-[#F59E0B]">,</code>, valores em R$ (vírgula decimal), datas DD/MM/AAAA ou ISO. Município é vinculado automaticamente pelo código IBGE ou pelo nome.
            </div>
          </label>

          <button
            data-testid="upload-csv-button"
            disabled={!file || busy}
            onClick={upload}
            className="mt-5 w-full px-4 py-3 rounded-md bg-[#F59E0B] text-[#0F172A] font-medium hover:bg-[#FCD34D] transition-colors disabled:opacity-50"
          >
            {busy ? "Importando…" : "Iniciar importação"}
          </button>

          {result && (
            <div className="mt-5 p-4 rounded-md border border-[#10B981]/30 bg-[#10B981]/10">
              <div className="flex items-center gap-2 text-[#10B981] text-sm">
                <CheckCircle size={18} weight="fill" />
                {result.imported} contratos importados com sucesso
              </div>
              {result.errors?.length > 0 && (
                <div className="mt-2 text-xs text-[#F59E0B]">
                  {result.errors.length} avisos: {result.errors.slice(0, 3).join(" · ")}
                </div>
              )}
            </div>
          )}
        </div>

        {/* TCE-CE API */}
        <div className="tce-card p-7">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-md bg-[#1E293B] flex items-center justify-center text-[#F59E0B]">
              <CloudArrowDown size={20} weight="duotone" />
            </div>
            <div>
              <div className="uppercase-label">Integração oficial</div>
              <div className="font-heading text-xl mt-1">Sincronizar com TCE-CE</div>
            </div>
          </div>

          <p className="text-sm text-[#94A3B8] leading-relaxed mb-6">
            Baixe a planilha oficial de contratos em <a href="https://www.tce.ce.gov.br/transparencia/dados-abertos" target="_blank" rel="noreferrer" className="text-[#F59E0B] hover:text-[#FCD34D] underline">tce.ce.gov.br/transparencia/dados-abertos</a> ou use a API SIM abaixo. Esta sincronização tenta a API pública diretamente — se indisponível, faça upload do CSV ao lado.
          </p>

          <div className="rounded-md bg-[#0F172A] border border-[#334155] p-4 mb-5">
            <div className="uppercase-label mb-1">Endpoint base</div>
            <code className="text-xs text-[#F59E0B] font-mono break-all">
              https://api-dados-abertos.tce.ce.gov.br/sim/
            </code>
          </div>

          <button
            data-testid="sync-tce-button"
            disabled={busy}
            onClick={syncTCE}
            className="w-full px-4 py-3 rounded-md border border-[#F59E0B] text-[#F59E0B] font-medium hover:bg-[#F59E0B]/10 transition-colors disabled:opacity-50"
          >
            {busy ? "Conectando…" : "Sincronizar agora"}
          </button>

          {syncResult && (
            <div className={`mt-5 p-4 rounded-md border ${
              syncResult.ok
                ? "border-[#10B981]/30 bg-[#10B981]/10 text-[#10B981]"
                : "border-[#F59E0B]/30 bg-[#F59E0B]/10 text-[#F59E0B]"
            }`}>
              <div className="flex items-start gap-2 text-sm">
                {syncResult.ok ? <CheckCircle size={18} weight="fill" /> : <Warning size={18} weight="fill" />}
                <span>{syncResult.message}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* CSV template */}
      <div className="tce-card p-6">
        <div className="uppercase-label mb-3">Modelo de CSV</div>
        <pre className="text-xs text-[#94A3B8] overflow-x-auto bg-[#0F172A] p-4 rounded border border-[#334155] font-mono">
{`numero,objeto,fornecedor,cnpj_fornecedor,modalidade,valor,data_inicio,data_fim,status,municipio_id,municipio,orgao
2024/001,"Aquisição de medicamentos","Farma Brasil LTDA","12345678/0001-90","Pregão Eletrônico",125000.50,2024-01-15,2025-01-14,ativo,2304400,Fortaleza,Secretaria de Saúde`}
        </pre>
      </div>
    </div>
  );
}
